<script setup lang="ts">
  import { computed, onMounted, onUnmounted, ref } from 'vue';
  import { useModal } from 'vue-final-modal';
  import { ColoredButton, FieldSearchBar, FilterSortAndDelete, TableTitle } from '@/shared/ui';
  import PlusIcon from '@/shared/assets/icons/PlusIcon.svg';
  import ExportExcelIcon from '@/shared/assets/icons/ExportExcel.svg';
  import AdminListPageLayout from '../AdminListPageLayout.vue';
  import MembersTable from './MembersTable.vue';
  import MemberFilterModal from './MemberFilterModal.vue';
  import CreateMemberModal from './CreateMemberModal.vue';
  import EditMemberModal from './EditMemberModal.vue';
  import { useAdminListPageSetup, createAdminListPageProps } from '../../lib';
  import { useItemSelection } from '../../lib/useItemSelection';
  import { useMemberDelete } from '../../lib/useMemberDelete';
  import { useTableFilterModal } from '@/shared/lib';
  import { memberSearchFieldOptions, memberOrderOptions, type AdminMember } from '../../model';
  import { searchAdminMembers } from '../../api';
  import { useMembersExport } from './useMembersExport';
  import type { MemberSearchField } from '@/shared/lib';

  const props = defineProps(createAdminListPageProps());

  const searchFieldId = ref<MemberSearchField>('name');

  const emit = defineEmits<{ create: []; export: [] }>();

  const {
    items: members,
    loading,
    handleSearch,
    initSearch,
    metadata,
    currentPage,
    selectedQuantity,
    onPageChanged,
    handleQuantityChange,
    searchTerm,
    cleanup,
    handleSortOptions,
    sort,
    order,
    removeItems,
    handleFilter,
    isUsingFilters,
    filter,
  } = useAdminListPageSetup<AdminMember>(
    {
      defaultSearchUrl: '/members',
      searchFunction: searchAdminMembers,
      defaultAmount: 10,
      searchFieldRef: searchFieldId,
    },
    props
  );

  const { isFilterActive, openFilterModal } = useTableFilterModal(
    MemberFilterModal,
    { clearFilters: () => {}, filters: {} },
    handleFilter,
    () => filter.value
  );

  const {
    selectedCount,
    hasSelected,
    isSelected,
    toggleSelection,
    selectedItems,
    deselectAll,
  } = useItemSelection<AdminMember>();

  const { handleExportToExcel, isExporting } = useMembersExport({
    searchTerm,
    searchFieldId,
    sort,
    order,
    filter,
  });

  const editMemberId = ref<number | null>(null);

  const editModal = useModal({
    component: EditMemberModal,
    attrs: {
      get memberId() {
        return editMemberId.value ?? 0;
      },
      onClose: () => {
        editModal.close();
        editMemberId.value = null;
      },
      onSaved: async () => {
        editModal.close();
        editMemberId.value = null;
        await initSearch();
      },
    },
  });

  const handleEditMember = (id: number) => {
    editMemberId.value = id;
    editModal.open();
  };

  const allSelected = computed(
    () =>
      (members.value?.length ?? 0) > 0 &&
      members.value!.every((m) => isSelected(m.id))
  );

  const handleToggleAll = () => {
    const list = members.value ?? [];
    const allCurrentlySelected =
      list.length > 0 && list.every((m) => isSelected(m.id));
    if (allCurrentlySelected) {
      deselectAll();
    } else {
      list.forEach((m) => {
        if (!isSelected(m.id)) {
          toggleSelection(m.id);
        }
      });
    }
  };

  const hasItems = computed(() => (members.value?.length ?? 0) > 0);

  const { openDeleteConfirmationModal: openBulkDeleteModal } = useMemberDelete(
    () => selectedCount.value,
    () => selectedItems.value,
    (deletedIds) => {
      removeItems(deletedIds);
    },
    (ids) => ids.forEach((id) => toggleSelection(id))
  );

  const pendingSingleDeleteId = ref<number | null>(null);
  const { openDeleteConfirmationModal: openSingleDeleteModal } = useMemberDelete(
    () => 1,
    () => [pendingSingleDeleteId.value!],
    (deletedIds) => {
      removeItems(deletedIds);
    },
    (ids) => ids.filter((id) => isSelected(id)).forEach((id) => toggleSelection(id))
  );

  function handleSingleDelete(id: number) {
    pendingSingleDeleteId.value = id;
    openSingleDeleteModal();
  }

  const createMemberModal = useModal({
    component: CreateMemberModal,
    attrs: {
      onClose() {
        createMemberModal.close();
      },
      onSaved() {
        initSearch();
      },
    },
  });

  onMounted(async () => {
    await initSearch();
  });

  onUnmounted(() => {
    cleanup();
  });

  defineExpose(
    import.meta.env.MODE === 'test'
      ? {
          testHandleToggleAll: handleToggleAll,
          testAllSelected: () => allSelected.value,
          testSelectedCount: () => selectedCount.value,
          testToggleSelection: toggleSelection,
          testIsSelected: isSelected,
        }
      : {}
  );
</script>

<template>
  <AdminListPageLayout
    data-testid="admin-members-page"
    title="Membros"
    create-button-text="Novo membro"
    variant="stacked"
    entity-name="membro"
    no-filter-results-message="Nenhum membro encontrado com os filtros aplicados"
    empty-state-message="Nenhum membro cadastrado"
    :items="members ?? []"
    :loading="loading"
    :search-term="searchTerm"
    :metadata="metadata"
    :selected-quantity="selectedQuantity"
    :is-using-filters="isUsingFilters"
    :current-page="currentPage"
    @search="handleSearch"
    @page-changed="onPageChanged"
    @update:selected-quantity="handleQuantityChange"
    @export="() => {}"
  >
    <template #action-buttons>
      <ColoredButton
        :text="'Novo membro'"
        :icon="PlusIcon"
        icon-alt="Ícone de adição"
        class="h-11 rounded-lg"
        @click="() => { emit('create'); createMemberModal.open(); }"
      />
      <ColoredButton
        text="Exportar para Excel"
        :icon="ExportExcelIcon"
        icon-alt="Ícone de exportação"
        class="h-11 rounded-lg"
        :disabled="!hasItems || isExporting"
        @click="() => { emit('export'); handleExportToExcel(); }"
      />
    </template>
    <template #search="{ onSearch }">
      <FieldSearchBar
        v-model:search-field-id="searchFieldId"
        independent-search-field
        :search-fields="memberSearchFieldOptions"
        :initial-search-term="searchTerm"
        class="flex-1"
        @search="onSearch"
      />
    </template>

    <template #table>
      <div
        class="mt-6 w-full border border-gray-02 rounded-2xl overflow-hidden bg-white-01"
      >
        <div
          class="px-6 py-4 flex w-full items-center justify-between border-b border-gray-02"
        >
          <TableTitle text="Lista de Membros" />
          <FilterSortAndDelete
            :has-selected="hasSelected"
            :selected-count="selectedCount"
            :selected-documents="selectedItems"
            :order-options="memberOrderOptions"
            :current-sort="sort"
            :current-order="order"
            :is-filter-active="isFilterActive"
            has-filter
            :delete-disabled="!hasSelected"
            @update:sort="handleSortOptions"
            @filter-clicked="openFilterModal"
            @delete="openBulkDeleteModal"
          />
        </div>
        <MembersTable
          :members="members ?? []"
          :is-selected="isSelected"
          :all-selected="allSelected"
          :search-term="searchTerm"
          :is-filter-active="isFilterActive"
          :loading="loading"
          @toggle-selection="toggleSelection"
          @toggle-all="handleToggleAll"
          @delete="handleSingleDelete"
          @edit="handleEditMember"
        />
      </div>
    </template>
  </AdminListPageLayout>
</template>
